import { Moment } from 'moment';
import { IJobArea } from 'src/app/maintenance-admin/models/job-area.model';
import { IAttachment } from 'src/app/shared/attachments/models/attachments.model';
import { IdName } from 'src/app/shared/models/id-name';
import { IdNameSfi } from 'src/app/shared/models/id-name-sfi.model';
import { ProjectStatus } from '../enums/project-statatus.enum';
import { ICorrectiveActionPerformSummary } from './corrective-action.model';

type Nullable<T> = T | null;

export enum MaintenanceObjectKind {
  MaintenanceJob = 0,
  CorrectiveAction = 1,
}
export interface IProjectInfo {
  id: string;
  name: string;
  status: ProjectStatus;
  isExported: boolean;
  canOpenDraft: boolean;
}

export interface IMaintenanceJob {
  id: string;
  name: string;
  jobNumber?: number;
  jobArea?: IJobArea;
  jobType: IdName;
  description: string;
  assignmentsWithAccess: IdName[];
  positionsWithAccess: IdName[];
  positionGroupsWithAccess: IdName[];
  responsibleForPositions: IdName[];
  responsibleForPositionGroups: IdName[];
  alert: IdName;
  customForm: IdName;
  runtime: IdName;
  component: IdNameSfi;
  attachments?: IAttachment[];
  legacyData?: IAttachment[];
  expiryType?: 'IntervalExpiry' | 'RuntimeExpiry' | 'OneTimeExpiry' | 'OneTimeExpiryAndRuntime';
  intervalExpiry?: { interval: number; lastServiceDate: Date; nextServiceDate?: Date };
  runTimeExpiry?: { runtimeId: string; serviceInterval: number; runTimeAtLastService: number };
  oneTimeExpiry?: { dueDate: Date };
  draftAvailable: boolean;
  isDisabled: boolean;
  isCompleted: boolean;
  standardJobTemplate: IdName;
  createdFromStandardJobTemplateVersion: IdName;
  standardJobTemplateDescription: string;
  standardJobTemplateAttachments: string;
  rolesWithAccess: IdName[];
  responsibleForRoles: IdName[];
  priceDetails?: { price: number; currency: IdName; content: string };
  canPerform: boolean;
  projectInfo?: IProjectInfo;
  kind?: MaintenanceObjectKind;
  performs?: ICorrectiveActionPerformSummary[];
  pausable?: boolean;
  isPaused?: boolean;
  estimatedExpiryDate?: string;
  pauseStatusSnapshot?: string;
}

export interface IMaintenanceJobRequest {
  id?: string;
  name: string;
  jobNumber: number;
  jobAreaId?: string;
  standardJobTemplateId: string;
  jobTypeId: string;
  description: string;
  assignmentsWithAccess: string[];
  positionsWithAccess: string[];
  positionGroupsWithAccess: string[];
  responsibleForPositionIds: string[];
  responsibleForPositionGroupIds: string[];
  rolesWithAccess: string[];
  responsibleForRoleIds: string[];
  alertId: string;
  customFormId: string;
  componentId: string;
  tenantId: string;
  attachments: IAttachment[];
  pausable?: boolean;
  intervalExpiry?: {
    interval: string;
    lastServiceDate: Date | string | Moment;
    nextServiceDate?: Date | string | Moment;
  };
  runTimeExpiry?: { runtimeId: string; serviceInterval: number; runTimeAtLastService: number };
  oneTimeExpiry?: { dueDate: Date | string | Moment };
  priceDetails?: { price: number; currencyId: string; content: string };
}

export interface IMaintenanceJobForTable {
  jobId: string;
  name: string;
  jobNumber: number;
  jobType: string;
  expiryType: string;
  interval?: { intervalExpiry?: Nullable<number>; runtimeExpiry?: Nullable<number> };
  timeUntilExpiry?: {
    intervalExpiry?: Nullable<number>;
    runtimeExpiry?: Nullable<{
      timeUntilExpiry?: Nullable<number>;
      avgDailyRuntime?: Nullable<number>;
    }>;
  };
  dueDateOrRuntime?: {
    intervalExpiry?: Nullable<string>;
    runtimeExpiry?: Nullable<{ dueDate?: Nullable<string>; hours?: Nullable<number> }>;
    oneTimeExpiry?: Nullable<string>;
  };
  status: string;
  pausable?: boolean;
  showPauseOverviewColumn?: boolean;
  isPaused?: boolean;
  estimatedExpiryDate?: string;
  pauseStatusSnapshot?: string;
  project?: IProjectInfo;
  // subheader for groups
  isSubHeader?: boolean;
  isMaintenanceJobGroup?: boolean;
  expiredJobsCount?: number;
  isE0Component?: boolean;
  isClassComponent?: boolean;
  classId?: string;
  isCriticalComponent?: boolean;
  menuId: string;
  canPerform: boolean;
  kind: MaintenanceObjectKind;
  performs?: ICorrectiveActionPerformSummary[];
}

export interface IMaintenanceJobLogChange {
  fieldName: string;
  oldValue: string;
  newValue: string;
}

export interface IMaintenanceJobLogEntry {
  date: string;
  extraInfo: string[];
  changes?: IMaintenanceJobLogChange[] | null;
  by: {
    id: string;
    name: string;
    positionId: string | null;
    positionName: string | null;
  };
  action: string;
  doneByMarineManager: boolean;
  attachment?: IAttachment;
  legacyData?: IAttachment;
  id: string;
}

export interface IMaintenanceJobGroupData {
  id: string;
  address: string;
  additionalData: {
    classId: string;
    expiredJobsCount: number;
    isClassComponent: boolean;
    isCriticalComponent: boolean;
    isE0Component: boolean;
  };
  itemsOnNode: IMaintenanceJobForTable[];
}

export interface IApiResponseGroupMaintenanceJobs {
  data: IMaintenanceJobForTable[];
  totalPages: number;
  totalItems: number;
  groupData: IMaintenanceJobGroupData[];
}
